import React from 'react'
import moment from 'moment'
import {Badge} from 'react-bootstrap'
import {defineMessages, useIntl} from 'react-intl'
import {NodePolicy} from '../../../types/policiesApi'
import {InfoClientPolicy} from './InfoClientPolicy'
import {BadgeStatePolicy} from '../../onePolicy/components/BadgeStatePolicy'
import {UserAvatar} from '../../../components/UserAvatar'
import {getPolicyFormKind, isAcaKind} from '../../../helpers/policyFormKind'
import {
  getBadgeTextColor,
  getCarrierStatusVariant,
  getDocumentVariant,
  getPaymentVariant,
} from '../../../helpers/consts'

// Anotacion de codigo de estado del carrier: no se traduce.
const inForceTag = ' (IN_FORCE)'

// Claves nuevas de esta vista. El resto se reusa del catalogo con el helper `f()`
// (ya declaradas en PoliciesPage / ModalFilters), por eso no se repiten aca.
const M = defineMessages({
  assignedTo: {
    id: 'policies.list.card.assignedTo',
    defaultMessage: 'Assigned to {name}',
  },
})

interface IPolicyMobileCardProps {
  policy: NodePolicy
  statusLabel: string
  statusColor: string
  selected: boolean
  /** Solo en la pestana Archived: agrega la fecha de archivado al pie. */
  showArchivedDate?: boolean
  /** Dropdown de acciones que arma PoliciesPage (mismo nodo que usa la tabla). */
  actions: React.ReactNode
  onToggleSelected: () => void
  onToggleImportant: () => void
  onOpenCarrierStatus: () => void
}

/**
 * Version movil de una fila de la tabla de polizas.
 *
 * En pantallas chicas la tabla obliga a hacer scroll horizontal, asi que se
 * muestra la misma poliza como card. El contenido se limita a lo que un agente
 * necesita para reconocer y priorizar la poliza sin abrirla: cliente, poliza
 * (carrier / plan / vigencia), estado operativo (status, documentos, pago,
 * carrier) y de quien es. El resto del detalle sigue en Preview y en la ficha.
 */
export const PolicyMobileCard: React.FC<IPolicyMobileCardProps> = ({
  policy,
  statusLabel,
  statusColor,
  selected,
  showArchivedDate,
  actions,
  onToggleSelected,
  onToggleImportant,
  onOpenCarrierStatus,
}) => {
  const intl = useIntl()
  const f = (id: string, defaultMessage: string, values?: Record<string, any>) =>
    intl.formatMessage({id, defaultMessage}, values)

  const naValue = f('common.value.notAvailable', 'N/A')
  const nrValue = f('policies.list.preview.value.nr', 'N/R')

  const isAca = isAcaKind(getPolicyFormKind(policy.typePolicy))
  const ownerName = `${policy.user?.firstName ?? ''} ${policy.user?.lastName ?? ''}`.trim()
  const assignedName = policy.assignedTo
    ? `${policy.assignedTo.firstName ?? ''} ${policy.assignedTo.lastName ?? ''}`.trim()
    : ''
  const documentsVariant = getDocumentVariant(policy.stateDocs)
  const paymentVariant = getPaymentVariant(policy.paymentStatus as unknown as string)
  const docsAreOverdue =
    !!policy.documentsDueDate &&
    moment(policy.documentsDueDate).startOf('day').isBefore(moment().startOf('day'))

  return (
    <div className='policy-card'>
      <div className='policy-card__head'>
        <div className='policy-card__marks'>
          <i
            className='bi bi-star-fill fs-3'
            style={{color: policy.isImportant ? 'gold' : '#c9cdd7', cursor: 'pointer'}}
            onClick={onToggleImportant}
          />
          <input
            className='form-check-input'
            type='checkbox'
            checked={selected}
            onChange={onToggleSelected}
          />
        </div>
        <div className='policy-card__client'>
          <InfoClientPolicy
            id={policy.id}
            holder={policy.holderMember}
            policy={policy}
            isQuote={false}
          />
        </div>
        <div className='policy-card__status'>
          <BadgeStatePolicy status={statusLabel} color={statusColor} />
        </div>
      </div>

      {/* Dos columnas: datos de la poliza a la izquierda y los estados a la
          derecha, alineados bajo el badge de status del encabezado. */}
      <div className='policy-card__body'>
        <div className='policy-card__policy'>
          <span className='policy-card__carrier'>{policy.carrier?.name || nrValue}</span>
          {!isAca && policy.planName && (
            <span className='policy-card__plan' title={policy.planName}>
              {policy.planName}
            </span>
          )}
          <span className='policy-card__effective'>
            {f('policies.list.page.cell.effective', 'Effective date: {date}', {
              date: moment(policy.effectiveDate).format('MMM DD, YYYY'),
            })}
          </span>

          <div className='policy-card__tags'>
            <Badge
              bg='primary'
              className='d-flex align-items-center gap-1 text-white'
              title={f(
                'policies.list.page.titleApplicants',
                '{count, plural, one {# Applicant} other {# Applicants}}',
                {
                  count: policy.applicants || 0,
                }
              )}
            >
              <i className='bi bi-people-fill' />
              {policy.applicants || 0}
            </Badge>
            {policy.memberId && (
              <span className='policy-card__member-id'>
                {f('policies.list.page.cell.memberId', 'ID: {id}', {id: policy.memberId})}
              </span>
            )}
            {policy.hasAutoPolicy === true ? (
              <Badge
                bg='success'
                className='d-flex align-items-center text-white'
                title={f('policies.list.filters.field.hasAutoPolicy', 'Has auto policy')}
              >
                <i className='bi bi-car-front-fill' style={{color: 'var(--bs-white)'}} />
              </Badge>
            ) : policy.ownsCar === true ? (
              <Badge
                bg='danger'
                className='d-flex align-items-center text-white'
                title={f('policies.list.filters.field.ownsCar', 'Owns car')}
              >
                <i className='bi bi-car-front-fill' style={{color: 'var(--bs-white)'}} />
              </Badge>
            ) : null}
            {policy.hasHomePolicy === true ? (
              <Badge
                bg='success'
                className='d-flex align-items-center text-white'
                title={f('policies.list.filters.field.hasHomePolicy', 'Has home policy')}
              >
                <i className='bi bi-house-fill' style={{color: 'var(--bs-white)'}} />
              </Badge>
            ) : policy.ownsHome === true ? (
              <Badge
                bg='danger'
                className='d-flex align-items-center text-white'
                title={f('policies.list.filters.field.ownsHome', 'Owns home')}
              >
                <i className='bi bi-house-fill' style={{color: 'var(--bs-white)'}} />
              </Badge>
            ) : null}
            {policy.hasCarrierAccount === true && (
              <Badge
                bg={policy.accountCreatedByClient === true ? 'info' : 'success'}
                className='d-flex align-items-center justify-content-center text-white'
                style={{width: '1.5rem', height: '1.5rem', padding: 0}}
                title={
                  policy.accountCreatedByClient === true
                    ? f(
                        'policies.list.filters.field.hasCarrierAccount.createdByClient',
                        'Has carrier account (created by client)'
                      )
                    : f('policies.list.filters.field.hasCarrierAccount', 'Has carrier account')
                }
              >
                <i
                  className='bi bi-link-45deg'
                  style={{color: 'var(--bs-white)', fontSize: '1.1rem'}}
                />
              </Badge>
            )}
          </div>
        </div>

        <div className='policy-card__states'>
          <div className='policy-card__state'>
            <span className='policy-card__label'>
              {f('policies.list.page.cell.documents', 'Documents:')}
            </span>
            <Badge
              className='badge-sm'
              bg={documentsVariant}
              text={getBadgeTextColor(documentsVariant)}
            >
              {policy.stateDocs || naValue}
            </Badge>
          </div>
          <div className='policy-card__state'>
            <span className='policy-card__label'>
              {f('policies.list.page.cell.payment', 'Payment:')}
            </span>
            <Badge
              className='badge-sm'
              bg={paymentVariant}
              text={getBadgeTextColor(paymentVariant)}
            >
              {(policy.paymentStatus as unknown as string) || naValue}
            </Badge>
          </div>
          {policy.documentsDueDate && (
            <div className='policy-card__state'>
              <span className='policy-card__label'>
                {f('policies.list.page.cell.docsDue', 'Documents due date:')}
              </span>
              <Badge
                className='badge-sm'
                bg={docsAreOverdue ? 'danger' : 'warning'}
                text={docsAreOverdue ? 'white' : 'dark'}
              >
                {moment(policy.documentsDueDate).format('MMM DD, YYYY')}
              </Badge>
            </div>
          )}
          {policy.carrierStatusGeneric && (
            <div className='policy-card__state'>
              <span className='policy-card__label'>
                {f('policies.list.page.cell.carrier', 'Carrier:')}
              </span>
              <Badge
                className='badge-sm'
                bg={getCarrierStatusVariant(policy.carrierStatusGeneric)}
                text={getBadgeTextColor(getCarrierStatusVariant(policy.carrierStatusGeneric))}
                style={{cursor: 'pointer'}}
                onClick={onOpenCarrierStatus}
              >
                {policy.carrierStatusGeneric === 'IN_FORCE' && policy.carrierStatusRaw
                  ? policy.carrierStatusRaw + inForceTag
                  : policy.carrierStatusRaw || policy.carrierStatusGeneric}
              </Badge>
              {policy.carrierIsAgeIn && (
                <Badge className='badge-sm' bg='info' text='white'>
                  {f('policies.list.page.cell.ageIn', 'AGE-IN')}
                </Badge>
              )}
              {policy.carrierIsDelinquent && policy.carrierStatusGeneric !== 'DELINQUENT' && (
                <Badge className='badge-sm' bg='warning' text='dark'>
                  {f('policies.list.page.cell.dlq', 'DLQ')}
                </Badge>
              )}
            </div>
          )}
          {policy.advisorPaid && (
            <div className='policy-card__state'>
              <Badge className='badge-sm' bg='success'>
                {f('policies.list.page.cell.paidToAdvisor', 'PAID TO ADVISOR')}
              </Badge>
            </div>
          )}
          {policy.advisorChargeback && (
            <div className='policy-card__state'>
              <Badge className='badge-sm' bg='danger' text='white'>
                {f('policies.list.page.cell.chargeback', 'CHARGEBACK')}
              </Badge>
            </div>
          )}
          {showArchivedDate && policy.archivedDate && (
            <div className='policy-card__state'>
              <span className='policy-card__label'>
                {f('policies.list.page.menu.archived', 'Archived')}
              </span>
              <Badge className='badge-sm' bg='secondary' text='dark'>
                {moment(policy.archivedDate).format('MMM DD, YYYY')}
              </Badge>
            </div>
          )}
        </div>
      </div>

      <div className='policy-card__foot'>
        <div className='policy-card__owner'>
          <UserAvatar user={policy.user} size={32} />
          <div className='policy-card__owner-names'>
            <span className='policy-card__owner-name'>{ownerName || naValue}</span>
            {assignedName && (
              <span className='policy-card__assigned'>
                {intl.formatMessage(M.assignedTo, {name: assignedName})}
              </span>
            )}
          </div>
        </div>
        <div className='policy-card__actions'>{actions}</div>
      </div>
    </div>
  )
}
